/**
 * FunShine Multiplayer Engine
 * Manages Room creation, joining, Supabase Realtime (Presence & Broadcast),
 * Action Sequencing, Turn Enforcement, Disconnection & State Recovery.
 */
import { supabase, isSupabaseConfigured } from './supabase.js';
import { auth } from './auth.js';

const CODE_CHARS = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

export function generateRoomCode() {
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += CODE_CHARS.charAt(Math.floor(Math.random() * CODE_CHARS.length));
  }
  return result;
}

export class MultiplayerSession {
  constructor(roomData, isHost) {
    this.room = roomData;
    this.isHost = isHost;
    this.currentMatch = null;
    this.channel = null;
    this.broadcastFallback = null;
    this.actionSeq = 0;
    this.receivedActionIds = new Set();
    this.listeners = {
      action: [],
      presence: [],
      matchStart: [],
      matchEnd: [],
      opponentLeft: []
    };
    this.peers = new Map();
  }

  async setupRealtime() {
    const roomId = this.room.id || this.room.code;
    const profile = auth.getProfile();
    const userId = auth.getUserId();

    // Setup local BroadcastChannel fallback for multi-tab testing & offline operation
    if (typeof window !== 'undefined' && window.BroadcastChannel) {
      try {
        this.broadcastFallback = new BroadcastChannel(`funshine_room_${this.room.code}`);
        this.broadcastFallback.onmessage = (event) => {
          this.handleIncomingMessage(event.data);
        };
      } catch (e) {
        console.warn('[Multiplayer] BroadcastChannel not supported:', e);
      }
    }

    if (!isSupabaseConfigured()) {
      console.log('[Multiplayer] Running in local/tab mode (Supabase not connected)');
      return;
    }

    // Subscribe to Supabase Realtime Channel
    this.channel = supabase.channel(`room_${this.room.code}`, {
      config: {
        broadcast: { ack: true, self: false },
        presence: { key: userId }
      }
    });

    this.channel
      .on('broadcast', { event: 'game_action' }, ({ payload }) => {
        this.handleIncomingMessage(payload);
      })
      .on('presence', { event: 'sync' }, () => {
        const state = this.channel.presenceState();
        this.handlePresenceSync(state);
      })
      .on('presence', { event: 'join' }, ({ newPresences }) => {
        for (const p of newPresences) {
          this.peers.set(p.user_id, p);
        }
        this.emit('presence', { peers: Array.from(this.peers.values()), event: 'join' });
      })
      .on('presence', { event: 'leave' }, ({ leftPresences }) => {
        for (const p of leftPresences) {
          this.peers.delete(p.user_id);
        }
        this.emit('presence', { peers: Array.from(this.peers.values()), event: 'leave' });
        this.emit('opponentLeft', { leftPresences });
      });

    await this.channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await this.channel.track({
          user_id: userId,
          display_name: profile.display_name,
          avatar_emoji: profile.avatar_emoji,
          is_host: this.isHost,
          joined_at: new Date().toISOString()
        });
      }
    });
  }

  handlePresenceSync(presenceState) {
    this.peers.clear();
    for (const key of Object.keys(presenceState)) {
      const presences = presenceState[key];
      if (presences && presences.length > 0) {
        const p = presences[0];
        this.peers.set(p.user_id, p);
      }
    }
    this.emit('presence', { peers: Array.from(this.peers.values()), event: 'sync' });
  }

  handleIncomingMessage(data) {
    if (!data || !data.type) return;

    // Check duplicate action
    const actionKey = `${data.senderId}_${data.seq}_${data.type}`;
    if (this.receivedActionIds.has(actionKey)) return;
    this.receivedActionIds.add(actionKey);

    if (data.type === 'action') {
      this.emit('action', data);
    } else if (data.type === 'match_start') {
      this.currentMatch = data.match;
      this.emit('matchStart', data.match);
    } else if (data.type === 'match_end') {
      this.emit('matchEnd', data.result);
    }
  }

  async sendAction(type, payload) {
    this.actionSeq++;
    const message = {
      type: 'action',
      actionType: type,
      payload,
      senderId: auth.getUserId(),
      seq: this.actionSeq,
      timestamp: Date.now()
    };

    // Broadcast via Realtime channel
    if (this.channel) {
      await this.channel.send({
        type: 'broadcast',
        event: 'game_action',
        payload: message
      });
    }

    // Also dispatch to local BroadcastChannel (for 2-tab local test)
    if (this.broadcastFallback) {
      try {
        this.broadcastFallback.postMessage(message);
      } catch (e) {}
    }

    // Persist to Supabase match_actions if match is active
    if (isSupabaseConfigured() && this.currentMatch?.id) {
      supabase
        .from('match_actions')
        .insert({
          match_id: this.currentMatch.id,
          player_id: auth.getUserId(),
          sequence: this.actionSeq,
          action_type: type,
          payload
        })
        .then(() => {})
        .catch((err) => console.warn('[Multiplayer] Action persist err:', err));
    }
  }

  async broadcastMatchStart(matchData) {
    this.currentMatch = matchData;
    const msg = {
      type: 'match_start',
      match: matchData,
      senderId: auth.getUserId(),
      seq: ++this.actionSeq
    };

    if (this.channel) {
      await this.channel.send({
        type: 'broadcast',
        event: 'game_action',
        payload: msg
      });
    }
    if (this.broadcastFallback) {
      try {
        this.broadcastFallback.postMessage(msg);
      } catch (e) {}
    }
  }

  async broadcastMatchEnd(result) {
    const msg = {
      type: 'match_end',
      result,
      senderId: auth.getUserId(),
      seq: ++this.actionSeq
    };

    if (this.channel) {
      await this.channel.send({
        type: 'broadcast',
        event: 'game_action',
        payload: msg
      });
    }
    if (this.broadcastFallback) {
      try {
        this.broadcastFallback.postMessage(msg);
      } catch (e) {}
    }
  }

  on(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback);
    }
    return () => {
      this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback);
    };
  }

  emit(event, data) {
    if (this.listeners[event]) {
      for (const cb of this.listeners[event]) {
        try {
          cb(data);
        } catch (e) {
          console.error(`[Multiplayer] Listener error on ${event}:`, e);
        }
      }
    }
  }

  async leave() {
    if (this.channel) {
      try {
        await this.channel.untrack();
        await this.channel.unsubscribe();
      } catch (e) {}
      this.channel = null;
    }
    if (this.broadcastFallback) {
      try {
        this.broadcastFallback.close();
      } catch (e) {}
      this.broadcastFallback = null;
    }
  }
}

/**
 * Multiplayer Service: API for creating and joining rooms
 */
class MultiplayerService {
  async createRoom(gameId, config = {}) {
    await auth.init();
    const userId = auth.getUserId();
    const code = generateRoomCode();

    const roomRecord = {
      code,
      game_id: gameId,
      host_id: userId,
      status: 'waiting',
      config
    };

    if (isSupabaseConfigured()) {
      const { data, error } = await supabase
        .from('rooms')
        .insert(roomRecord)
        .select()
        .single();

      if (error) {
        console.warn('[Multiplayer] Failed to create remote room, using local room:', error.message);
      } else {
        const session = new MultiplayerSession(data, true);
        await session.setupRealtime();
        return session;
      }
    }

    // Local / tab fallback
    const localRoom = { ...roomRecord, id: 'local_' + code };
    const session = new MultiplayerSession(localRoom, true);
    await session.setupRealtime();
    return session;
  }

  async joinRoom(roomCode) {
    await auth.init();
    const code = roomCode.trim().toUpperCase();
    const userId = auth.getUserId();

    if (isSupabaseConfigured()) {
      const { data: room, error } = await supabase
        .from('rooms')
        .select('*')
        .eq('code', code)
        .maybeSingle();

      if (error || !room) {
        throw new Error(`Room "${code}" not found or has expired.`);
      }

      if (room.status === 'finished') {
        throw new Error('This room has already ended.');
      }

      // If joining as guest (and not already the host)
      if (room.host_id !== userId && !room.guest_id) {
        await supabase
          .from('rooms')
          .update({ guest_id: userId, status: 'ready', updated_at: new Date().toISOString() })
          .eq('id', room.id);
        room.guest_id = userId;
        room.status = 'ready';
      }

      const isHost = room.host_id === userId;
      const session = new MultiplayerSession(room, isHost);
      await session.setupRealtime();
      return session;
    }

    // Local fallback
    const localRoom = {
      id: 'local_' + code,
      code,
      game_id: 'gomoku',
      host_id: 'host_' + code,
      guest_id: userId,
      status: 'ready',
      config: {}
    };
    const session = new MultiplayerSession(localRoom, false);
    await session.setupRealtime();
    return session;
  }

  async startMatch(session) {
    const userId = auth.getUserId();
    const room = session.room;
    const guestId = room.guest_id || 'guest_player';

    const matchRecord = {
      room_id: room.id.startsWith('local_') ? null : room.id,
      game_id: room.game_id,
      player1_id: room.host_id,
      player2_id: guestId,
      current_turn: room.host_id,
      status: 'playing',
      state: {},
      action_count: 0
    };

    if (isSupabaseConfigured() && !room.id.startsWith('local_')) {
      const { data, error } = await supabase
        .from('matches')
        .insert(matchRecord)
        .select()
        .single();

      if (!error && data) {
        await supabase
          .from('rooms')
          .update({ status: 'playing' })
          .eq('id', room.id);

        await session.broadcastMatchStart(data);
        return data;
      }
    }

    // Local match fallback
    const localMatch = {
      id: 'local_match_' + Date.now(),
      ...matchRecord
    };
    await session.broadcastMatchStart(localMatch);
    return localMatch;
  }
}

export const multiplayer = new MultiplayerService();
